import React from "react";
import {
  Item,
  Line,
  Svg,
  TextNode as RPDFCTextNode,
  View,
  TextNode,
} from "@surge-global-engineering/react-pdf-components";
import { StyleSheet } from "@surge-global-engineering/rpdf-renderer";
import { MEDIUM_MARGIN, SMALL_MARGIN } from "../configs";
import { PdfChapterEndnotes, PdfEndnoteSubheading, PdfSlateEndnote, PdfSlateFootnote, PdfSlateTextNode, PdfTheme } from "../types";
import Paragraph from "./Paragraph";
import { getChapterHeaderFontStyles, getHeaderSelectorStyle } from "../helpers";
import { Chapter } from "../../../../types/chapter";

interface EndNotesProps {
  content: (PdfSlateEndnote | PdfEndnoteSubheading | PdfChapterEndnotes)[];
  theme: PdfTheme;
  nodeIndex: number;
  endnoteOffset?: number;
  chapter?: Chapter
}

const EndNotes: React.FC<EndNotesProps> = ({
  content,
  theme,
  nodeIndex,
  endnoteOffset = 0,
  chapter
}) => {
  const { paragraph, baseFontSize, printBaseFont , footnoteFontSize, notesMode, pdfEndnotesChapterSettings} = theme.properties;
  const { paragraphSpacing, indent } = paragraph;

  const filteredEndnotes = (content as PdfSlateEndnote[]).filter((note) => note.type === "endnote");

  const styles = StyleSheet.create({
    svg: {
      marginTop: indent ? MEDIUM_MARGIN : SMALL_MARGIN,
    },
    text: {
      fontFamily: printBaseFont,
      fontSize: baseFontSize * footnoteFontSize,
      lineHeight: paragraphSpacing,
      marginBottom: "10px"
    },
    subheading: {
      fontWeight: 600,
    }
  });

  const hyphenationCallback = (word: string) => [word];

  return (
    <View>
      {notesMode !== "END_OF_BOOK" && (
        <Svg height="20" style={styles.svg}>
          <Line
            x1="0"
            x2="1000"
            y1="0"
            y2="0"
            strokeWidth={2}
            stroke="#000000"
          ></Line>
        </Svg>
      )}

      {pdfEndnotesChapterSettings.showTitle && notesMode === "END_OF_BOOK" ?
        <TextNode
          {...getChapterHeaderFontStyles("chapterTitle", theme.properties)}
          style={
            [
              { marginBottom: "30px" },
              getHeaderSelectorStyle(
                "chapterTitle",
                theme.properties,
                chapter as Chapter
              ),

            ]}
          hyphenationCallback={hyphenationCallback}
        >
          {chapter?.title}
        </TextNode>
        : undefined}

      {pdfEndnotesChapterSettings.categorizeByTitle && notesMode === "END_OF_BOOK"? 
      (content as PdfChapterEndnotes[]).map((item, index) => {
        return (
          <React.Fragment key={`chapter-${index}`}>
            <RPDFCTextNode
              key={`endnote-heading-${index}`}
              style={item.type === "chapter" ? 
              [styles.text] : 
              [styles.text, styles.subheading]}
              hyphenationCallback={hyphenationCallback}
            >
              {item.chapterTitle}
            </RPDFCTextNode>
            {item.chapterEndnotes.map((chapterEndnote, i) => {
              // Remove empty text nodes from the link
              let endnotes: PdfSlateTextNode[] = [];
              if ((chapterEndnote.note[0] as PdfSlateFootnote).children) {
                endnotes = (chapterEndnote.note[0] as PdfSlateFootnote).children.filter(child => child.text != "");
              }
              return (
                <Item
                  wrap={chapterEndnote.note?.length > 500}
                  key={`endnote-${index}-${i}`}
                  style={styles.text}
                  prefix={`${
                    i + 1 + endnoteOffset
                  }. `}
                >
                  {!(
                    (chapterEndnote.note[0] as PdfSlateFootnote).children
                  ) ? (
                    <RPDFCTextNode
                      style={styles.text}
                      hyphenationCallback={hyphenationCallback}
                    >
                      {chapterEndnote.note}
                    </RPDFCTextNode>
                  ) : (
                    <Paragraph
                      key={`end-base-${nodeIndex + index}-${i}`}
                      theme={theme}
                      nodeIndex={nodeIndex}
                      parentType="endnote-chapter"
                      showFirstSentenceFormatting={false}
                    >
                      {endnotes}
                    </Paragraph>
                  )}
                </Item>
              );
            })}
          </React.Fragment>
        );       
    })
    : <>
    {(content as (PdfSlateEndnote | PdfEndnoteSubheading)[]).map((
      item: PdfSlateEndnote | PdfEndnoteSubheading, 
      index: number) => {
        if (item.type === "endnote-subheading") {
          return (
            <RPDFCTextNode
              key={`endnote-heading-${index}`}
              style={[styles.text, styles.subheading]}
              hyphenationCallback={hyphenationCallback}
            >
              {item.text}
            </RPDFCTextNode>
          );
        }
      // Remove empty text nodes from the link
      let endnotes: PdfSlateTextNode[] = [];
      if ((item.note[0] as PdfSlateFootnote).children) {
        endnotes = (item.note[0] as PdfSlateFootnote).children.filter(child => child.text != "");
      }  
        return (
          <Item
            wrap={item.note?.length > 500}
            key={`endnote-${index}`}
            style={styles.text}
            prefix={`${
              filteredEndnotes.findIndex(
                (candidateItem) => candidateItem === item
              ) +
              1 +
              endnoteOffset
            }. `}
          >
            { !(item.note[0] as PdfSlateFootnote).children ? (
              <RPDFCTextNode
                style={styles.text}
                hyphenationCallback={hyphenationCallback}
              >
                {item.note}
              </RPDFCTextNode>
            ) : (
              <Paragraph
                key={`end-base-${nodeIndex + index}`}
                theme={theme}
                nodeIndex={nodeIndex}
                parentType="endnote-chapter"
                showFirstSentenceFormatting={false}
              >
                {endnotes}
              </Paragraph>
            )}
          </Item>
        );
      })}
    </>}
    </View>
  );
};

export default EndNotes;
